import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getSearchesPageContent } from "services/getSearchesPageContent";

import styles from "./SearchesPage.module.scss";
import LoadingPage from "views/LoadingPage";

const SearchesPage = () => {
  const [t] = useTranslation("global");
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await getSearchesPageContent();
        const grouped = data.reduce((acc, item) => {
          const key = item.type;
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        }, {});
        setGroupedData(grouped);
      } catch (err) {
        setError(err.message || "An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const sortedData = useMemo(() => {
    return Object.entries(groupedData).map(([key, items]) => ({
      key,
      items: items.sort((a, b) => a.title.localeCompare(b.title)),
    }));
  }, [groupedData]);

  if (loading) {
    return (
     <LoadingPage/>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <div className={styles.errorIcon}>⚠️</div>
        <h3>{t("common.error") || "Error"}</h3>
        <p>{error}</p>
        <button 
          className={styles.retryButton}
          onClick={() => window.location.reload()}
        >
          {t("common.retry") || "Retry"}
        </button>
      </div>
    );
  }

  return (
    <section className={`${styles.searches} pageContainer`}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{t("searchesList.title")}</h1>
      </header>

      <div className={styles.searchWrapper}>
        {sortedData.map(({ key, items }) => (
          <article className={styles.category} key={key}>
            <header className={styles.categoryHeader}>
              <h2 className={styles.categoryTitle}>
                {t(`searchesList.${key}`)}
              </h2>
            </header>
            <nav aria-label={`Links for ${t(`searchesList.${key}`)}`}>
              <ul className={styles.searchList}>
                {items.map((search) => (
                  <li key={search.url} className={styles.searchItem}>
                    <Link 
                      to={`/search/${search.url}`}
                      className={styles.searchLink}
                    >
                      {search.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </article>
        ))}
      </div>
    </section>
  );
};

export default SearchesPage;